import { ContentType } from '@/@types/common'
import { PostFormValue } from '@/components/Post/PostForm/schemas'
import { RepostFormValue } from '@/components/Post/RepostForm/types'

import { compact, isArray, isEmpty, isPlainObject, omitBy } from 'lodash-es'
import { useWatch } from 'react-hook-form'
import { useLocalStorage } from 'react-use'

export type DraftFormValue<Type extends DraftType> = {
	[ContentType.POST]: PostFormValue
	[ContentType.REPOST]: RepostFormValue
}[Type]
export type DraftType = ContentType.POST | ContentType.REPOST

function omitEmptyDeep<T extends object>(value: T | undefined | null): T {
	return omitBy(
		value,
		(value: T[keyof T]) =>
			isEmpty(value) ||
			(isPlainObject(value) && isEmpty(omitEmptyDeep(value as object))) ||
			(isArray(value) && isEmpty(value))
	) as T
}

export default function usePostDraft(type: DraftType) {
	const formValues = useWatch<DraftFormValue<typeof type>>()
	const id =
		type === ContentType.REPOST
			? (formValues as RepostFormValue).targetId
			: (formValues as PostFormValue).id
	const [draft, setDraft, removeDraft] = useLocalStorage<DraftFormValue<typeof type>>(
		compact([`${type}_DRAFT`, id]).join('_')
	)

	const noDraft = isEmpty(omitEmptyDeep(draft))
	return [draft, setDraft, removeDraft, noDraft] as const
}
